<template>
    <Modal id="session-picker-modal"
           size="xl"
           icon="fa-calendar-days"
           :class="additionalClasses"
           :custom-title-text="title"
           :cancel="cancel"
           :ok="ok"
           :ok-enabled="okButtonEnabled()"
           :ok-label-raw="okLabel"
           :ok-spinner="priceLoading">
        <div v-if="error"
             class="alert alert-warning">
            <i class="fa-solid fa-triangle-exclamation"></i>
            {{ l(error) }}
        </div>

        <div v-if="autoSubmitPreferredSession && preferredSessionIsBookabke === false"
             class="alert alert-danger">
            <i class="fa-solid fa-triangle-exclamation"></i>
            <template v-if="preferredSessionHasNoResourcesAvailable">
                {{ l('SESSION_PICKER.PREFERRED_SESSION_UNAVAILABLE') }}
            </template>
            <template v-else-if="preferredSessionIsFull">
                {{ l('SESSION_PICKER.PREFERRED_SESSION_FULL') }}
            </template>
            <template v-else>
                {{ l('SESSION_PICKER.PREFERRED_SESSION_FULL') }}
            </template>
        </div>

        <!-- Show a loading spinner saying the session is being checked if there is a preferred session that will be
        auto submitted once it's ready. This lasts until the availability for the first 3 months has been loaded, and
        the price of the preferred session has been checked. -->
        <div v-if="autoSubmitPreferredSession && !preferredSessionAvailabilityAndPriceChecked"
             class="session-picker__loading-overlay">
            <PageLoadingSpinner lang="SESSION_PICKER.CHECKING_PREFERRED_SESSION" />
        </div>
        <!-- Show a loading spinner to show the first 3 months of sessions are being loaded. -->
        <div v-else-if="initialMonthsLoaded !== true"
             class="session-picker__loading-overlay">
            <PageLoadingSpinner lang="SESSION_PICKER.LOADING_SESSIONS" />
        </div>
        <div v-else
             class="session-picker__content">
            <div class="session-picker__day-btns">
                <div class="session-picker__day-btns-row">
                    <button class="btn btn-default"
                            :class="{active: selectedDateString === todaysDateYMD}"
                            :disabled="!sessionsAvailableToday"
                            @click.prevent="setSelectedDate(todaysDate)">
                        {{ l('SESSION_PICKER.TODAY_BTN') }}
                    </button>
                    <button class="btn btn-default"
                            :class="{active: selectedDateString === tomorrowsDateYMD}"
                            :disabled="!sessionsAvailableTomorrow"
                            @click.prevent="setSelectedDate(tomorrowsDate)">
                        {{ l('SESSION_PICKER.TOMORROW_BTN') }}
                    </button>

                    <button v-if="firstDateWithSessions"
                            class="btn btn-default"
                            :class="{active: selectedDateString === firstDateWithSessionsYMD}"
                            @click.prevent="setSelectedDate(firstDateWithSessions)">
                        {{ l('SESSION_PICKER.FIRST_DATE_BTN') }}
                    </button>
                </div>

                <div class="session-picker__day-btns-row flex--row-reverse--mobile">
                    <button class="btn btn-default"
                            :disabled="!nextDateWithSessions"
                            @click.prevent="setSelectedDate(nextDateWithSessions)">
                        {{ l('SESSION_PICKER.NEXT_DATE_BTN') }}
                        <i class="mobile-only fa-solid fa-chevron-right"></i>
                    </button>
                    <button class="btn btn-default"
                            :disabled="!previousDateWithSessions"
                            @click.prevent="setSelectedDate(previousDateWithSessions)">
                        <i class="mobile-only fa-solid fa-chevron-left"></i>
                        {{ l('SESSION_PICKER.PREVIOUS_DATE_BTN') }}
                    </button>
                </div>
            </div>
            <div class="session-picker__calendar"
                 :class="{'session-picker__calendar--loading': sessionCalendar.monthLoadingCount > 0}">
                <i v-if="sessionCalendar.monthLoadingCount > 0"
                   class="fa-solid fa-spinner fa-spin"></i>
                <VueDatePick v-model="selectedDateString"
                             :is-date-disabled="isDateDisabled"
                             :has-input-element="false"
                             @update:model-value="onCalendarDateSelected"
                             @period-change="onDatePickPeriodChange" />
            </div>
            <div ref="sessions"
                 class="session-picker__sessions">
                <p class="session-picker__sessions-heading">
                    <span v-if="selectedDayHasInactiveSessions"
                          class="session-picker__has-propoint-only-label">
                        ({{ l('SESSION_PICKER.PROPOINT_ONLY') }}
                        <span class="fa-solid fa-stop session-inactive-indicator"></span>)
                    </span>
                    {{ l('SESSION_PICKER.SESSIONS_FOR') }} {{ dateFilter(selectedDate) }}
                </p>

                <div v-if="sessionsForSelectedDay.length > 0"
                     id="session-time-list"
                     ref="sessionTimeList"
                     class="list-group">
                    <SessionPickerSession v-for="(session, index) in sessionsForSelectedDay"
                                          :key="session.ID"
                                          :session="session"
                                          :session-number="(index + 1)"
                                          :event="event"
                                          :selected-session="selectedSession"
                                          :spaces-required="spacesRequired"
                                          :bookable-spaces="sessionCalendar.getBookableSpacesForSession(session.ID)"
                                          @select-session="selectSession" />
                </div>
                <div v-else-if="!error"
                     class="alert alert-info">
                    <i class="fa-solid fa-circle-exclamation"></i>
                    {{ l('SESSION_PICKER.NO_SESSIONS_AVAILABLE') }}
                    <template v-if="nextDateWithSessions">
                        <br />
                        <a class="btn btn-sm btn-default mt-05"
                           @click.prevent="setSelectedDate(nextDateWithSessions)">
                            <i class="fa-solid fa-arrow-right"></i>
                            {{ l('SESSION_PICKER.NEXT_DATE_BTN') }}
                        </a>
                    </template>
                </div>
            </div>
        </div>
    </Modal>
</template>

<script>
import BigNumber from 'bignumber.js';
import logFactory from '@/libraries/DigiTickets/Logging/logFactory';
import Modal from '@/vue/components/Modals/Modal';
import moment from 'moment';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import SessionPickerSession from '@/epos/vue/modals/SessionPicker/SessionPickerSession';
import VueDatePick from '@/vue/components/DatePicker/VueDatePick/VueDatePick';
import { container } from '@/container';
import { createSessionSalesCutoffFilterForEvent } from '@/lib/Sessions/sessionSalesCutoffFilter';
import { currencyFilter } from '@/vue/filters/currencyFilter';
import { isMobile } from '@/functions/screen';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { sortByStartTime } from '@/functions/arraySorting';
import { toYMD } from '@/functions/date';

export default {
    components: {
        Modal,
        PageLoadingSpinner,
        SessionPickerSession,
        VueDatePick,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * @type {SessionEvent}
         */
        event: {
            type: Object,
            required: true,
        },

        /**
         * @type {ItemRequest[]}
         */
        requestedTickets: {
            type: Array,
            required: true,
        },

        /**
         * @type {EventSessionPreferences}
         */
        eventSessionPreferences: {
            type: Object,
            default: null,
        },

        /**
         * @type {Object<number>}
         */
        sessionSpaceAdjustments: {
            type: Object,
            required: true,
        },

        /**
         * If the preferred session (from the eventSessionPreferences prop) is available,
         * automatically dismiss this modal and continue as if the OK button was pressed.
         * This is done once pricing rules and bookable resource availability have been applied.
         */
        autoSubmitPreferredSession: {
            type: Boolean,
            default: false,
        },

        highlightWhenPreferredSessionSelected: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            error: null,

            /**
             * An array of YYYY-MM strings to say which months have had their sessions loaded from IndexedDB
             * and the availability loaded from the API (if online).
             *
             * @type {string[]}
             */
            monthsLoaded: [],

            /**
             * Have the initial 3 months of sessions (current month + prev and next months) been loaded?
             * This is used as a v-if for the content of the modal. So the modal content, including the
             * Vue date picker, does not even render until this is true.
             */
            initialMonthsLoaded: false,

            /**
             * Show a loading spinner on the OK button?
             */
            priceLoading: false,

            /**
             * @type {SessionCalendar}
             */
            sessionCalendar: container.sessionCalendarPopulator.create(
                this.event.ID,
                createSessionSalesCutoffFilterForEvent(this.event)
            ),

            /**
             * @type {SessionSpaceCalculator}
             */
            sessionSpaceCalculator: null,

            /**
             * @type {ConsoleLogger}
             */
            log: null,

            /**
             * A list of all dates that contain *any* sessions for the event.
             * This is only used for the next/prev/first buttons, and does
             * not take into account remaining availability or bookable resources.
             *
             * @type {DateList}
             */
            dateList: null,

            /**
             * Today's date. Used for jumping to today and displaying the 'Today' button as active when appropriate.
             *
             * @type {Date}
             */
            todaysDate: new Date(),

            /**
             * Tomorrow's date. This is set to the correct date in beforeCreate().
             *
             * @type {Date}
             */
            tomorrowsDate: null,

            /**
             * The currently selected date in the calendar. This is a string due to how vue-date-pick works.
             * This starts as null but will be set to the first date that we know has any sessions (regardless of
             * availability) once that list of dates is loaded.
             *
             * @type {?string}
             */
            selectedDateString: null,

            /**
             * This could be a computed property, but there was an issue where the session list is not populated
             * when the modal first opens. So instead this is populated by the updateSessionsForSelectedDay method.
             */
            sessionsForSelectedDay: [],

            /**
             * ticketLines forms the basis of what we'll eventually return once the modal is closed.
             * It contains all the information needed to add the tickets to the cart (bar the session,
             * which is held in $scope.selectedSession).
             *   [
             *     { ticket, qty, price },
             *     { ticket, qty, price },
             *     ...etc
             *   ]
             */
            ticketLines: this.requestedTickets.map(
                /**
                 * @param {ItemRequest} r
                 *
                 * @return {ItemRequest}
                 */
                (r) => r.clone()
            ),

            /**
             * @type {?Session}
             */
            selectedSession: null,

            /**
             * @type {?Session}
             */
            preferredSession: this.eventSessionPreferences
                ? this.eventSessionPreferences.getPreferredSessionForEvent(this.event.ID)
                : null,

            /**
             * Is the preferred session still bookable?
             */
            preferredSessionIsBookabke: null,
            preferredSessionIsFull: null,
            preferredSessionHasNoResourcesAvailable: null,

            preferredSessionAvailabilityAndPriceChecked: false,
        };
    },

    computed: {
        /**
         * vue-date-pick sets this.selectedDateString as a string. This computed property provides that as a Date.
         */
        selectedDate() {
            return new Date(this.selectedDateString);
        },

        /**
         * Total number of tickets requested.
         */
        numTickets() {
            return this.ticketLines.reduce((total, { qty }) => total + qty, 0);
        },

        /**
         * Returns the total cost for all the requested tickets.
         * This might change depending on the session selected due to pricing rules.
         */
        totalPrice() {
            let total = new BigNumber(0);
            this.ticketLines.forEach(({
                price,
                qty,
            }) => {
                total = total.plus(price * qty);
            });

            return total.toNumber();
        },

        /**
         * Total number of spaces (people) required.
         */
        spacesRequired() {
            return this.ticketLines.reduce((total, {
                item,
                qty,
            }) => total + (qty * item.people), 0);
        },

        title() {
            return this.event.name
                + ' ('
                + this.numTickets
                + ' '
                + container.langService.getText(this.numTickets === 1 ? 'TICKET' : 'TICKETS')
                + ', '
                + this.spacesRequired
                + ' '
                + container.langService.getText(this.spacesRequired === 1 ? 'PERSON' : 'PEOPLE')
                + ')';
        },

        okLabel() {
            let str = container.langService.getText('SESSION_PICKER.ADD_TICKET');

            if (this.selectedSession) {
                str += ` (${currencyFilter(this.totalPrice)})`;
            }

            return str;
        },

        /**
         * Are there Inactive sessions for the current day? Used to toggle visibility of the key on the session picker.
         *
         * @return {boolean}
         */
        selectedDayHasInactiveSessions() {
            return this.sessionsForSelectedDay.some((session) => session.status === 'Inactive');
        },

        /**
         * Determines if the "Today" button is active.
         *
         * @type {boolean}
         */
        sessionsAvailableToday() {
            return this.dateList && this.dateList.hasDate(this.todaysDate);
        },

        /**
         * Determines if the "Tomorrow" button is active.
         *
         * @type {boolean}
         */
        sessionsAvailableTomorrow() {
            return this.dateList && this.dateList.hasDate(this.tomorrowsDate);
        },

        /**
         * @returns {string|null}
         */
        firstDateWithSessions() {
            if (!this.dateList) {
                return null;
            }

            return this.dateList.getFirstDate();
        },

        previousDateWithSessions() {
            if (!this.dateList) {
                return null;
            }

            return this.dateList.getPreviousDate(this.selectedDate);
        },

        nextDateWithSessions() {
            if (!this.dateList) {
                return null;
            }

            return this.dateList.getNextDate(this.selectedDate);
        },

        preferredSessionIsSelected() {
            return this.preferredSession
                && this.selectedSession
                && (this.preferredSession.ID === this.selectedSession.ID);
        },

        additionalClasses() {
            return [
                this.highlightWhenPreferredSessionSelected && this.preferredSessionIsSelected ? 'preferred-session-selected' : '',
            ].filter((v) => !!v);
        },

        todaysDateYMD() {
            return toYMD(this.todaysDate);
        },

        tomorrowsDateYMD() {
            return toYMD(this.tomorrowsDate);
        },

        firstDateWithSessionsYMD() {
            return this.firstDateWithSessions ? toYMD(this.firstDateWithSessions) : null;
        },
    },

    watch: {
        /**
         * When the selected date in the calendar changes.
         */
        selectedDateString() {
            // When changing the date on the calendar unselect the session so
            // the user can't accidentally submit with the wrong session selected that they can't see.
            if (this.selectedSession && this.selectedSession.startTimeYMD !== this.selectedDateString) {
                this.log.debug('Clearing selected session due to selected day change.');
                this.selectSession(null);
            }

            this.updateSessionsForSelectedDay();
        },
    },

    created() {
        // We use SessionSpaceCalculatorFactory and create a new SessionSpaceCalculator instance each time,
        // because the calculator is stateful and depends on the contents of the cart at the time it is created.
        this.sessionSpaceCalculator = container.sessionSpaceCalculatorFactory.create();
        this.sessionSpaceCalculator.setSessionSpaceAdjustments(this.sessionSpaceAdjustments);

        this.log = logFactory('SessionPickerModal');

        this.sessionCalendar.onSessionsAdded = () => {
            this.updateSessionsForSelectedDay();
        };

        this.tomorrowsDate = new Date();
        this.tomorrowsDate.setDate(this.tomorrowsDate.getDate() + 1);

        // Load the list of what dates have any sessions on them (regardless of availability).
        container.sessionRepository
            .getDaysForEvent(
                this.event.ID,
                container.userService.getItemBranchID(),
            )
            .then((dateList) => {
                this.dateList = dateList;

                // If there is a preferred session select the date for that session in the calendar now.
                // But don't select that session yet - we'll do that below.
                if (this.preferredSession) {
                    this.setSelectedDate(this.preferredSession.startTimeYMD);
                } else {
                    // Get the first date (today or later) that has any sessions.
                    const nextDate = dateList.getNextDate(this.todaysDate, true);
                    if (nextDate) {
                        this.setSelectedDate(nextDate);
                    } else {
                        // There are no days today or in the future that have and sessions.
                        // But we've got to select something. So use today's date.
                        this.setSelectedDate(this.todaysDate);
                    }
                }

                // Load the initial months of sessions.
                this.loadSessionsForMonthWithNeighbours(this.selectedDate).then(() => {
                    this.initialMonthsLoaded = true;

                    // Update the displayed sessions for the selected day now that they have been loaded.
                    // This will auto select the first session on the selected day.
                    this.updateSessionsForSelectedDay();

                    // If there is a preferred session select that one, overriding a session that may have just
                    // been auto selected.
                    if (this.preferredSession) {
                        this.log.debug('Selecting preferred session from cart', this.preferredSession);
                        this.selectSession(this.preferredSession)
                            .then(() => {
                                this.preferredSessionIsBookabke = true;

                                // Once pricing rules have been checked, auto submit with the preferred session
                                // if that is desired.
                                if (this.autoSubmitPreferredSession) {
                                    this.ok();
                                }
                            })
                            .catch(() => {
                                this.preferredSessionIsBookabke = false;

                                // Try to figure out why it's unavailable.
                                if (this.sessionCalendar.isSessionUnavailable(this.preferredSession.ID)) {
                                    this.preferredSessionHasNoResourcesAvailable = true;
                                }
                                if (this.sessionCalendar.getActualBookableSpacesForSession(this.preferredSession.ID) < this.spacesRequired) {
                                    this.preferredSessionIsFull = true;
                                }
                            })
                            .finally(() => {
                                this.preferredSessionAvailabilityAndPriceChecked = true;
                            });
                    }
                    // Otherwise, leave the first session on the selected day as selected.
                });
            });
    },

    methods: {
        onDatePickPeriodChange(newPeriod) {
            const newDate = new Date(newPeriod.year, newPeriod.month, 1, 0, 0, 0, 0);
            this.loadSessionsForMonthWithNeighbours(newDate);
        },

        /**
         * Scrolls the modal down to the session list when a date is clicked in the calendar on mobile,
         * because the list is below the calendar and would otherwise be out of view.
         */
        onCalendarDateSelected() {
            if (!isMobile()) {
                return;
            }

            // Wait for the new day's sessions to render, so the list is its full height before scrolling.
            this.$nextTick(() => {
                this.$refs.sessions?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            });
        },

        /**
         * Load the sessions and their availability for the given month, and 1 month either side.
         *
         * @param {Date} date
         */
        loadSessionsForMonthWithNeighbours(date) {
            const previousMonth = moment(date).subtract(1, 'months').toDate();
            const nextMonth = moment(date).add(1, 'months').toDate();

            return Promise.all(
                this.loadSessionsForMonths([
                    previousMonth,
                    date,
                    nextMonth,
                ])
            );
        },

        /**
         * @param {Date[]} dates
         *
         * @return {Promise<string>[]}
         */
        loadSessionsForMonths(dates) {
            return dates
                .map((d) => {
                    return container.sessionCalendarPopulator
                        .loadMonth(
                            this.sessionCalendar,
                            d,
                            this.requestedTickets,
                            this.sessionSpaceCalculator,
                            container.userService.getItemBranchID(),
                        );
                })
                // Filters out nulls that were returned because the month is already loaded.
                .filter((promise) => promise !== null)
                .map((promise) => {
                    return promise.then((ym) => {
                        this.monthsLoaded.push(ym);

                        return ym;
                    }).catch((e) => {
                        this.error = 'SESSION_PICKER.ERROR_LOADING_SESSIONS';

                        throw e;
                    });
                });
        },

        /**
         * This is a method instead of a computed prop because Vue wasn't playing nice with recomputing
         * the value when the dependencies change.
         *
         * Enable the OK button if there is a session selected and that session
         * has enough available spaces.
         *
         * @return {boolean}
         */
        okButtonEnabled() {
            return !!this.selectedSession && this.sessionHasEnoughSpaces(this.selectedSession);
        },

        ok() {
            if (!this.selectedSession) {
                return;
            }

            const result = this.ticketLines.map(
                /**
                 * @param {ItemRequest} itemRequest
                 *
                 * @return {{ticket: Ticket, price: Number, session: Session, qty: Number}}
                 */
                (itemRequest) => ({
                    ticket: itemRequest.item,
                    qty: itemRequest.qty,
                    price: itemRequest.price,
                    session: this.selectedSession,
                })
            );

            this.resolve(result);
        },

        /**
         * Select the given date in the calendar.
         * This may (indirectly) cause the selected session to be cleared.
         *
         * @param {Date|string} dateOrYmdString
         */
        setSelectedDate(dateOrYmdString) {
            if (dateOrYmdString instanceof Date) {
                this.selectedDateString = toYMD(dateOrYmdString);
            } else {
                this.selectedDateString = dateOrYmdString;
            }
        },

        /**
         * This method must be called to update the list of sessions shown on the right of the modal.
         * This sets 'sessionsForSelectedDay' to the list of all sessions for the selected date in the SessionCalendar,
         * and sorts the list by startTime/ID.
         */
        updateSessionsForSelectedDay() {
            const sessions = this.sessionCalendar.getSessionsForDay(this.selectedDate);

            // Sort session by startTime.
            sortByStartTime(sessions);

            this.sessionsForSelectedDay = sessions;

            // Select an appropriate session by default when changing day.
            if (!this.selectedSession && sessions.length > 0) {
                // Select the first session on the selected day by default.
                const session = sessions[0];
                this.log.debug(`Selecting first session on ${session.startTimeYMD}`);
                this.selectSession(session)
                    .catch(() => {});
            }

            this.$nextTick(() => {
                this.scrollToSelectedSession();
            });
        },

        /**
         * This method is called by vue-date-pick with an individual date to decide if that date
         * should be selectable in the calendar.
         *
         * @param {Date} date
         *
         * @returns {boolean}
         */
        isDateDisabled(date) {
            // Call isDateEnabled because it's easier logically to think about enabling dates rather than
            // disabling them.
            return !this.isDateEnabled(date);
        },

        isDateEnabled(date) {
            if (!this.sessionCalendar.hasSessionsForDay(date)) {
                // There are no sessions on this day (they might still be loading).
                return false;
            }

            const sessions = this.sessionCalendar.getSessionsForDay(date);

            for (let i = 0; i < sessions.length; i++) {
                let session = sessions[i];

                if (!this.sessionCalendar.availability.hasOwnProperty(session.ID)) {
                    // This day hasn't had its availability loaded.
                    // Because we can't currently be sure if this session is available or not it's best to show the day
                    // as available in case it is.
                    return true;
                }

                // The availability has been loaded for this session.
                if (this.sessionCalendar.availability[session.ID].bookableResourcesAvailable
                    && this.sessionCalendar.availability[session.ID].bookableResourcesSuitable) {
                    // This session is available and suitable, so the date contains at least one suitable session:
                    // TODO: Should we check there are available spaces? This would block out the day on the
                    // calendar if there are no sessions with spaces on that day. Maybe we don't want to do that.
                    return true;
                }
            }

            // No suitable sessions were found.
            return false;
        },

        /**
         * @param {Session} session
         *
         * @return {boolean}
         */
        sessionHasEnoughSpaces(session) {
            return this.sessionCalendar.getBookableSpacesForSession(session.ID) >= this.spacesRequired;
        },

        /**
         * @param {Session} session
         *
         * @return {Promise}
         */
        selectSession(session) {
            this.log.debug('Select session', session);

            this.selectedSession = session;

            if (!session) {
                // Always resolve when clearing the session.
                return Promise.resolve();
            }

            if (!this.sessionHasEnoughSpaces(session)) {
                this.log.debug('Cancelling session selection because there is not enough availability.');
                this.selectedSession = null;
                return Promise.reject();
            }

            // Make sure this is the selected day in the calendar.
            this.setSelectedDate(session.startTimeYMD);

            // When a session is selected, see if we need to update the price of any of the tickets.
            const pricePromises = this.ticketLines.map((itemRequest) => {
                return this.recalculateTicketPrice(session, itemRequest.item, itemRequest.qty)
                    .then((newOfflinePrice) => {
                        if (newOfflinePrice !== null) {
                            itemRequest.price = newOfflinePrice;
                        }
                    });
            });

            this.priceLoading = true;
            const allPricePromisesPromise = Promise.all(pricePromises);
            allPricePromisesPromise.finally(() => {
                this.priceLoading = false;
            });

            return allPricePromisesPromise;
        },

        scrollToSelectedSession() {
            if (this.selectedSession) {
                const sessionEl = document.getElementById(`session-${this.selectedSession.ID}`);
                if (sessionEl) {
                    this.$refs.sessionTimeList.scrollTop = Math.max(0, sessionEl.offsetTop - 20);
                }
            }
        },

        /**
         * This function works out whether it's worth asking the API about session-specific pricing. If it is, then
         * it will go away and work out an adjusted price for each ticket, and provide a total amount, stored in
         * Session.sessionPrice.
         *
         * @param {Session} session
         * @param {Ticket} ticket
         * @param {Number} qty
         *
         * @return {Promise<?Number>}
         */
        async recalculateTicketPrice(session, ticket, qty) {
            const itemBranch = container.userService.currentItemBranch;
            if (!itemBranch.pricingRulesActive) {
                // There aren't any pricing rules currently active.

                return null;
            }

            const { price } = await container.sessionPriceResource.query({
                ticketID: ticket.ID,
                qty,
                sessionID: session.ID,
                // Override the default branchID sent, which would be the order branch ID.
                branchID: itemBranch.ID,
            }).$promise;

            return parseFloat(price);
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../../less/variables";
@import '../../../../less/vendor/vue-date-pick/session-picker-date-pick.less';

#session-picker-modal {
    .session-picker__day-btns {
        .btn {
            display: block;
            width: 100%;
            margin-bottom: @half-gutter-width;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    .session-picker__day-btns-row {
        margin-bottom: @half-gutter-width;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .session-picker__calendar {
        position: relative;

        > i {
            position: absolute;
            // This isn't 50% because the calendar height can change depending on the number of days in the month.
            top: 200px;
            left: 50%;
            font-size: 24px;
            width: 24px;
            margin-left: -12px;
        }

        &--loading {
            .vdpComponent {
                opacity: .5;
            }
        }
    }

    .session-picker__sessions {
        #session-time-list {
            position: relative;
            font-size: 12px;
            margin: 0;
        }

        .session-inactive-indicator {
            color: @brand-warning;
        }

        .session-space-counts {
            float: right;
        }
    }

    .session-picker__sessions-heading {
        text-align: left;
        color: @text-muted;
    }

    .session-picker__has-propoint-only-label {
        float: right;
    }

    .date-pick-container {
        position: relative;

        > i {
            position: absolute;
            // This isn't 50% because the calendar height can change depending on the number of days in the month.
            top: 200px;
            left: 50%;
            font-size: 24px;
            width: 24px;
            margin-left: -12px;
        }

        &.date-pick-container--loading {
            .vdpComponent {
                opacity: .3;
            }
        }
    }

    &.preferred-session-selected {
        // The currently selected day and session are the preferred session.
        // This class is only added when it is requested to highlight when the preferred session is selected.
        // Dim other calendar days and other sessions.
        .session-picker-session:not(.active),
        .vdpCell:not(.selected) {
            opacity: .6;
        }
    }
}

@media (max-width: @mobile-max) {
    #session-picker-modal {
        .session-picker__day-btns {
            margin-bottom: @gutter-width-mobile;
        }

        .session-picker__day-btns-row {
            display: flex;
            gap: @half-gutter-width;

            .btn {
                flex: 1 1 0;
                margin-bottom: 0;
            }
        }

        .session-picker__sessions {
            margin-top: @gutter-width-mobile;
            // Leaves a gap above the heading when scrolled to, instead of it touching the modal header.
            scroll-margin-top: @gutter-width-mobile;
        }

        .session-picker__sessions-heading {
            text-align: center;
            display: flex;
            flex-direction: column-reverse;
        }

        .session-picker__has-propoint-only-label {
            float: none;
            display: block;
        }
    }
}

@media (min-width: @screen-md-min) {
    #session-picker-modal {
        .session-picker__content {
            display: flex;
        }

        .session-picker__day-btns {
            width: 120px;
            flex-grow: 0;
            flex-shrink: 1;
        }

        .session-picker__calendar {
            width: 40%;
            margin: 0 @gutter-width;
            flex-grow: 0;
            flex-shrink: 0;
        }

        .session-picker__sessions {
            flex-grow: 1;
            flex-shrink: 1;

            display: flex;
            flex-direction: column;

            #session-time-list {
                max-height: ~"calc(100dvh - 225px)";
                overflow: auto;
            }
        }
    }
}

@media (min-width: @screen-lg-min) {
    #session-picker-modal {
        .vdpComponent {
            .vdpCell {
                .vdpCellContent {
                    // Make the calendar buttons more square shaped when they are wider.
                    line-height: 60px;
                }
            }
        }
    }
}
</style>
